
import * as XLSX from 'xlsx';

export interface PriceData {
  productId: string;
  productName: string;
  msrp: string;
  [key: string]: string;
}

/**
 * Creates a downloadable Excel template for pricing data
 */
export const createPricingTemplate = () => {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet([
    {
      "Product Name": "VPX TC1 Pro Transceiver",
      "Product ID": "VPX-TC1-PRO",
      "MSRP": "999.99"
    },
    {
      "Product Name": "VPX TC1 LT Transceiver",
      "Product ID": "VPX-TC1-LT",
      "MSRP": "799.99"
    }
  ]);
  
  XLSX.utils.book_append_sheet(wb, ws, "Pricing");
  XLSX.writeFile(wb, "price_template.xlsx");
};

/**
 * Parses Excel file data into PriceData objects
 * Respects hidden rows in Excel files
 */
export const parseExcelPricing = (arrayBuffer: ArrayBuffer): PriceData[] => {
  console.log("Starting Excel parsing...");
  
  try {
    // Create workbook with raw option to access cell metadata (including hidden status)
    const workbook = XLSX.read(arrayBuffer, { 
      type: 'array',
      cellText: false, 
      cellDates: false,
      cellFormula: false,
      cellStyles: true, // Enable style parsing to detect hidden rows
    });
    
    if (!workbook.SheetNames.length) {
      console.error("No sheets found in Excel file");
      throw new Error("No sheets found in Excel file");
    }
    
    // Only use the first sheet
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    console.log("Sheet found:", sheetName);
    
    // Get the address range of the sheet
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    console.log("Sheet range:", range);
    
    // Get hidden rows information
    const hiddenRows = new Set<number>();
    if (worksheet['!rows']) {
      for (let i = 0; i <= range.e.r; ++i) {
        if (worksheet['!rows'][i]?.hidden) {
          hiddenRows.add(i);
          console.log("Found hidden row:", i);
        }
      }
    }
    
    // Convert to JSON with header mapping
    const rawData = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, {
      header: "A", // Use A, B, C as headers
      range: 0,    // Start from the first row
      raw: false   // Don't parse values (keep as strings)
    });
    
    console.log("Raw data rows:", rawData.length);
    
    // Skip header row
    const headers = rawData[0];
    console.log("Headers:", headers);
    
    // Find column indices for required fields
    let productIdCol = '';
    let productNameCol = '';
    let msrpCol = '';
    
    Object.entries(headers).forEach(([col, value]) => {
      const strValue = String(value).trim();
      if (strValue === 'Product ID') productIdCol = col;
      if (strValue === 'Product Name') productNameCol = col;
      if (strValue === 'MSRP') msrpCol = col;
    });
    
    console.log("Found columns:", { productIdCol, productNameCol, msrpCol });
    
    // Validate required columns exist
    if (!productIdCol || !productNameCol || !msrpCol) {
      console.error("Missing required columns");
      throw new Error("Missing required columns. Excel file must contain 'Product ID', 'Product Name', and 'MSRP' columns.");
    }
    
    // Filter out hidden rows and map to our interface
    const dataRows = rawData.slice(1); // Skip header row
    console.log("Data rows (before filtering):", dataRows.length);
    
    // Process each data row, skipping hidden rows
    const result: PriceData[] = [];
    
    // Process each data row, skipping hidden rows
    dataRows.forEach((row, index) => {
      // Skip if this row was hidden in Excel (add 1 because index 0 is the header row)
      if (hiddenRows.has(index + 1)) {
        console.log(`Skipping hidden row ${index + 1}`);
        return;
      }
      
      // Convert row data to strings and trim whitespace
      const productId = String(row[productIdCol] || '').trim();
      const productName = String(row[productNameCol] || '').trim();
      const msrp = String(row[msrpCol] || '').trim();
      
      // Only add rows that have at least product ID and name
      if (productId && productName) {
        result.push({
          productId,
          productName,
          msrp
        });
      } else {
        console.log(`Skipping row ${index + 1} due to missing required data`);
      }
    });
    
    console.log(`Final result: ${result.length} visible products processed`);
    return result;
  } catch (error) {
    console.error("Error parsing Excel file:", error);
    throw error;
  }
};

/**
 * Safely saves price data to localStorage
 */
export const savePriceDataToStorage = (key: string, data: PriceData[]): void => {
  try {
    const serializedData = JSON.stringify(data);
    localStorage.setItem(key, serializedData);
    console.log(`Saved ${data.length} products to localStorage with key: ${key}`);
  } catch (error) {
    console.error("Error saving to localStorage:", error);
    // Throw error to be handled by the caller
    throw new Error("Failed to save pricing data to storage");
  }
};

/**
 * Safely loads price data from localStorage
 */
export const loadPriceDataFromStorage = (key: string, fallbackData: PriceData[]): PriceData[] => {
  try {
    const storedData = localStorage.getItem(key);
    if (!storedData) {
      console.log("No stored data found, using fallback data");
      return fallbackData;
    }
    
    const parsedData = JSON.parse(storedData) as PriceData[];
    if (!Array.isArray(parsedData)) {
      console.error("Stored data is not an array, using fallback data");
      return fallbackData;
    }
    
    console.log(`Loaded ${parsedData.length} products from localStorage with key: ${key}`);
    return parsedData;
  } catch (error) {
    console.error("Error loading from localStorage:", error);
    return fallbackData;
  }
};
